import { 
  useAgentConversation,
  useConversationAnalysis,
  useConversationNavigation
} from './conversation';
import { ConversationMessage, ResponseLength, ScenarioType, TurnOrder } from '../types';
import { toast } from '@/hooks/use-toast';

export const useConversationFlow = (
  apiKey: string,
  agentAModel: string,
  agentBModel: string,
  agentCModel: string,
  agentAPersona: string,
  agentBPersona: string,
  agentCPersona: string,
  numberOfAgents: number,
  rounds: number,
  responseLength: ResponseLength,
  getCurrentScenario: () => ScenarioType,
  getCurrentPrompt: () => string,
  turnOrder: TurnOrder = 'sequential'
) => {
  // Use the navigation hook for view management
  const {
    currentView,
    setCurrentView,
    settingsOpen,
    setSettingsOpen
  } = useConversationNavigation();

  // Use the agent conversation hook for conversation management
  const {
    conversation,
    setConversation,
    isLoading,
    setIsLoading,
    handleStartConversation: startConversation
  } = useAgentConversation(
    apiKey,
    agentAModel,
    agentBModel,
    agentCModel,
    agentAPersona,
    agentBPersona,
    agentCPersona,
    numberOfAgents,
    rounds,
    responseLength,
    getCurrentScenario,
    getCurrentPrompt,
    turnOrder
  );

  // Use the analysis hook for conversation analysis
  const {
    isAnalyzing,
    setIsAnalyzing,
    analysisResults,
    setAnalysisResults,
    analyzerModel,
    setAnalyzerModel,
    handleAnalyzeConversation: analyzeConversation
  } = useConversationAnalysis(apiKey, conversation, undefined, undefined, undefined, undefined, getCurrentScenario()?.id);

  // Custom wrapper around startConversation that also updates the step
  const handleStartConversation = async () => {
    console.log("Starting conversation (shared key mode)...");
    console.log("- agentAModel:", agentAModel);
    console.log("- agentBModel:", agentBModel);
    console.log("- agentCModel:", agentCModel);
    console.log("- numberOfAgents:", numberOfAgents);
    
    // Set loading state immediately to prevent multiple clicks
    setIsLoading(true);
    
    // Check if we have valid models selected
    if (!agentAModel || (numberOfAgents >= 2 && !agentBModel) || (numberOfAgents >= 3 && !agentCModel)) {
      toast({
        title: "Models Required",
        description: "Please select models for all active agents.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    // Check if we have a valid prompt
    const currentPrompt = getCurrentPrompt();
    if (!currentPrompt.trim()) {
      toast({
        title: "Input Required",
        description: "Please enter text or a prompt for the agents to analyze.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    // Switch to conversation view
    setCurrentView('conversation');
    
    try {
      console.log("Starting conversation with shared key mode");
      await startConversation();
    } catch (error) {
      console.error("Error starting conversation:", error);
      
      // For errors, show a generic error message
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast({
        title: "Error Starting Conversation",
        description: errorMessage || "An error occurred while starting the conversation. Please try again.",
        variant: "destructive",
      });
      
      setIsLoading(false);
    }
  };

  // Custom wrapper around analyzeConversation that passes the current prompt
  const handleAnalyzeConversation = async (model?: string) => {
    console.log("Analyzing conversation (shared key mode)...");
    
    const currentPrompt = getCurrentPrompt();
    setIsAnalyzing(true);
    
    try {
      await analyzeConversation(model, currentPrompt);
    } catch (error) {
      console.error("Error analyzing conversation:", error);
      
      // For errors, show a generic error message
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast({
        title: "Error Analyzing Conversation",
        description: errorMessage || "An error occurred while analyzing the conversation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    conversation,
    setConversation,
    isLoading,
    setIsLoading,
    currentView,
    setCurrentView,
    settingsOpen,
    setSettingsOpen,
    isAnalyzing,
    setIsAnalyzing,
    analysisResults,
    setAnalysisResults,
    analyzerModel,
    setAnalyzerModel,
    handleStartConversation,
    handleAnalyzeConversation
  };
};
